import { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, Send, Star } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useLang } from '@/i18n/LangContext';
import { useSeo } from '@/components/Seo';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { appointmentTime } from '@/lib/appointments';
import { getPublicResource, submitPublicForm } from '@/lib/publicForms';

function Rating({ name, max, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: max + 1 }, (_, index) => index).filter((index) => max === 10 || index > 0).map((score) => (
        <button type="button" key={score} onClick={() => onChange(name, score)} aria-label={`${name}: ${score}`} className={`grid h-10 min-w-10 place-items-center rounded-md border px-2 text-sm font-bold ${value === score ? 'border-primary bg-primary text-white' : 'border-border bg-white hover:border-primary'}`}>
          {max === 5 ? <Star className={`h-4 w-4 ${value >= score ? 'fill-current' : ''}`} /> : score}
        </button>
      ))}
    </div>
  );
}

export default function AppointmentSurvey() {
  const { publicToken } = useParams();
  const { lang } = useLang();
  const en = lang === 'en';
  useSeo({ title: en ? 'Appointment feedback' : 'Feedback appuntamento', description: en ? 'Tell us how your appointment went.' : 'Raccontaci com’è andato il tuo appuntamento.', robots: 'noindex,nofollow' });
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState({ overall_rating: 0, consultant_clarity: 0, perceived_value: 0, recommendation_score: -1 });
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    getPublicResource(`/public/v1/appointment-surveys/${encodeURIComponent(publicToken)}`)
      .then(setData).catch((requestError) => setError(requestError.message)).finally(() => setLoading(false));
  }, [publicToken]);

  const setRating = (name, value) => setRatings((current) => ({ ...current, [name]: value }));
  const submit = async (event) => {
    event.preventDefault();
    if (ratings.overall_rating < 1 || ratings.consultant_clarity < 1 || ratings.perceived_value < 1 || ratings.recommendation_score < 0) {
      setError(en ? 'Please answer all four ratings.' : 'Rispondi a tutte le quattro valutazioni.');
      return;
    }
    setSending(true); setError('');
    try {
      setResult(await submitPublicForm(`/public/v1/appointment-surveys/${encodeURIComponent(publicToken)}`, { ...ratings, feedback: feedback.trim() || null }));
    } catch (requestError) { setError(requestError.message); }
    finally { setSending(false); }
  };

  if (loading) return <main className="grid min-h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></main>;
  if (result) return (
    <main className="mx-auto w-full max-w-2xl px-5 py-20 md:px-8">
      <div className="rounded-xl border border-primary/25 bg-brand-pale p-8 text-center md:p-12">
        <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
        <h1 className="mt-5 font-heading text-3xl font-bold">{en ? 'Thank you for your feedback!' : 'Grazie per il tuo feedback!'}</h1>
        <p className="mt-3 text-foreground/65">{en ? 'It helps us provide an even better service.' : 'Ci aiuta a offrire un servizio sempre migliore.'}</p>
        {result.google_review_url && <a href={result.google_review_url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-white">{en ? 'Share it on Google' : 'Condividilo anche su Google'} <ExternalLink className="h-4 w-4" /></a>}
      </div>
    </main>
  );

  return (
    <main className="mx-auto w-full max-w-2xl px-5 py-14 md:px-8 md:py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Unity Consulting</p>
      <h1 className="mt-3 font-heading text-4xl font-bold">{en ? 'How did it go?' : 'Com’è andato l’appuntamento?'}</h1>
      {data?.appointment && <p className="mt-3 text-sm text-foreground/60">{appointmentTime(data.appointment.starts_at, lang)} · {data.appointment.consultant_name}</p>}
      {error && !data && <div role="alert" className="mt-8 rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">{error}</div>}
      {data && <form onSubmit={submit} className="mt-9 space-y-7 rounded-xl border border-border bg-white p-6 md:p-8">
        {data.questions.filter((question) => question.type !== 'text').map((question) => <div key={question.name}><Label className="mb-3 block text-base font-bold">{question.label}</Label><Rating name={question.name} max={question.max} value={ratings[question.name]} onChange={setRating} /></div>)}
        <div><Label htmlFor="survey-feedback" className="mb-2 block text-base font-bold">{data.questions.find((question) => question.type === 'text')?.label}</Label><Textarea id="survey-feedback" rows={5} maxLength={2000} value={feedback} onChange={(event) => setFeedback(event.target.value)} /></div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <button type="submit" disabled={sending} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-white disabled:opacity-40">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{en ? 'Send feedback' : 'Invia feedback'}</button>
      </form>}
    </main>
  );
}
